import React, { useState, useEffect, useCallback } from "react";
import { LookupInput, type LookupInputOption, type LookupInputState } from "@/components/ui/LookupInput";
import { locationService } from "@/services/locationService";
import { useGlobalSearchParams } from "expo-router";
import { useNavigationHistory } from "@/contexts/NavigationHistoryContext";
import { useCurrentEntity } from "@/contexts/CurrentEntityContext";
import useNavigationGuard from "@/hooks/useNavigationGuard";

interface LocationInputProps {
  value: string | null;
  state?: LookupInputState;
  label?: string;
  error?: string;
  placeholder?: string;
  info?: string;
  onChange?: (locationId: string | null) => void;
}

export function LocationInput({
  value,
  state = "editing",
  label = "Location",
  error,
  placeholder = "Select location…",
  info,
  onChange,
}: LocationInputProps) {
  const currentParams = useGlobalSearchParams<{ panel?: string; id?: string }>();
  const { pushNavigation } = useNavigationHistory();
  const currentEntity = useCurrentEntity();
  const { navigateToEntity } = useNavigationGuard();

  const handleIdlePress = useCallback(() => {
    if (!value) return;
    const currentPanel = Array.isArray(currentParams.panel) ? currentParams.panel[0] : currentParams.panel;
    const currentId = Array.isArray(currentParams.id) ? currentParams.id[0] : currentParams.id;
    if (currentPanel && currentId) {
      pushNavigation({ panel: currentPanel, id: currentId, title: currentEntity?.currentTitle || "" });
    }
    navigateToEntity("location", value);
  }, [value, currentParams, pushNavigation, currentEntity, navigateToEntity]);

  const [options, setOptions] = useState<LookupInputOption[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const locations = await locationService.getLocations();
        if (cancelled) return;
        setOptions(
          locations.map((loc) => ({
            id: loc.id!,
            name: loc.name,
            subtitle: [loc.street, loc.city, loc.state, loc.zip].filter(Boolean).join(", ") || undefined,
            icon: "map-marker" as const,
          })),
        );
      } catch (err) {
        console.error("Failed to load locations:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const resolvedState: LookupInputState = loading ? "loading" : state;

  return (
    <LookupInput
      label={label}
      value={value}
      options={options}
      state={resolvedState}
      icon="map-marker"
      error={error}
      placeholder={placeholder}
      info={info}
      onChange={onChange}
      onIdlePress={handleIdlePress}
    />
  );
}
